import { useAuth0 } from '@auth0/auth0-react';
import React, { useContext, useEffect } from 'react';
import { generatePath, matchPath, useNavigate } from 'react-router-dom';

import { Loading } from '../../components/loading/loading';
import { Paths } from '../../constants/paths';
import { GlobalContext } from '../../context/global-context';
import { isDemoApp } from '../../utils/app';
import { CR_USER_PROP, TokenUtil } from '../../utils/token';

const Redirect: React.FC = () => {
  const navigate = useNavigate();
  const { demoAccessToken } = useContext(GlobalContext);

  const { user, getAccessTokenSilently, isLoading } = useAuth0();

  useEffect(() => {
    const handleRedirect = async () => {
      // Handle demo mode
      const isDemo = isDemoApp(location.pathname);
      if (isDemo && demoAccessToken) {
        const tokenExtracts = TokenUtil.extractUserProps(demoAccessToken);
        if (tokenExtracts) {
          navigate(generatePath(Paths.DEMO_MONITOR, { tenantId: String(tokenExtracts.tenant) }));
        }
        return;
      }

      // Wait for Auth0 to finish loading
      if (isLoading) return;

      // Handle regular authenticated user
      if (!user) {
        console.error('User not authenticated after Auth0 loading completed');
        // Redirect to login after a brief delay to avoid rapid loops
        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
        return;
      }

      // Extract tenant directly from user object (no token fetch needed)
      let targetTenant = user[CR_USER_PROP].tenant;
      const storedRouteJSON = localStorage.getItem('requestedRoute');
      const currentQueryParams = new URLSearchParams(location.search);

      // Check for stored route with different tenant
      if (storedRouteJSON) {
        try {
          const parsedRoute = JSON.parse(storedRouteJSON);
          const storedRoute = parsedRoute.pathname;
          const storedQueryParams = new URLSearchParams(parsedRoute.search);

          const sharePath = matchPath(Paths.SHARE, storedRoute);

          if (sharePath?.params?.tenantId) {
            const requestedTenant = Number(sharePath.params.tenantId);

            // Only fetch new token if tenant differs from user's default
            if (requestedTenant !== targetTenant) {
              try {
                await getAccessTokenSilently({
                  cacheMode: 'off',
                  authorizationParams: { tenant: requestedTenant },
                });
                targetTenant = requestedTenant;
              } catch (error) {
                console.error('Failed to get token for shared tenant:', error);
                // Fall back to user's default tenant
              }
            }
          }

          // Merge current and stored query parameters
          for (const [key, value] of storedQueryParams.entries()) {
            if (!currentQueryParams.has(key)) {
              currentQueryParams.set(key, value);
            }
          }

          // Clear the stored route from localStorage
          localStorage.removeItem('requestedRoute');
        } catch (error) {
          console.error('Failed to parse stored route:', error);
          localStorage.removeItem('requestedRoute');
        }
      }

      // Generate path with target tenant
      const path = generatePath(Paths.VIEWS, {
        tenantId: targetTenant,
      });

      // Append query parameters to the path
      const pathWithQuery = `${path}?${currentQueryParams.toString()}`;

      // Navigate to the final destination
      navigate(pathWithQuery);
    };

    handleRedirect();
  }, [user, isLoading, demoAccessToken, navigate, getAccessTokenSilently]);

  return <Loading />;
};

export default Redirect;
